import { useMutation, useQuery, useSubscription } from "@apollo/client";
import gql from "graphql-tag";
import * as Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { toastSuccessOptions } from "../../../App";
import getCurrentUserInformation from "../../../GraphQL/Query/getCurrentUserInformation";
import { formatPasswordChangeDate } from "../../../utils/formatPasswordChangedWhen";
import { handleGraphQLErrors } from "../../../utils/handleGraphQLError";
import { Button } from "../../SharedComponents/Button/Button";
import { DisplayPageError } from "../../SharedComponents/DisplayPageError/DisplayPageError";
import { EditableInformation } from "../../SharedComponents/EditableInformation/EditableInformation";
import { EditableInformationPassword } from "../../SharedComponents/EditableInformation/EditableInformationPassword/EditableInformationPassword";
import { EditableInformationText } from "../../SharedComponents/EditableInformation/EditableInformationText/EditableInformationText";
import { EditHeader } from "../../SharedComponents/EditHeader/EditHeader";
import { UserApps } from "./UserApps/UserApps";
import "./UserManageUser.css";

export const UserManageUser = () => {
  const { t } = useTranslation();
  const id = Cookies.get("primary_account");
  const { data: currentUserData } = useQuery(getCurrentUserInformation);
  /** @type {object} profileInformation */
  const [profileInformation, setProfileInformation] = useState({});
  /** @type {*} */
  const params = useParams();
  const { data, error, loading } = useQuery(getUserQuery, {
    variables: { id: params.id },
  });

  const history = useHistory();

  useSubscription(subscribeToUserUpdates, {
    variables: {
      userId: params.id,
    },
    onSubscriptionData: ({ client: { cache }, subscriptionData }) =>
      updateUserOnSubData({ cache, subscriptionData, params }),
  });

  useEffect(() => {
    if (data?.user) {
      setProfileInformation({ ...data.user, password: "********" });
    }
  }, [data]);

  const [updateUser] = useMutation(updateUserInformationMutation);

  if (loading) {
    return <div>Loading...</div>;
  }

  const canEdit =
    currentUserData?.user?.roles?.sso?.find((x) => x.accountId === id)?.role !==
    "SUPPORTER";

  const handleUpdateUser = (updatedData) => {
    updateUser({ variables: { id: data?.user?.id, ...updatedData } })
      .then(() => {
        toast(t("toast.userUpdated"), toastSuccessOptions);
      })
      .catch((err) => {
        handleGraphQLErrors(err);
      });
  };

  return (
    <div className="UserManageUser">
      <div className="UserManageUser__content">
        <div className="UserManageUser__content--inner">
          <EditHeader
            type="types.user"
            name={
              profileInformation?.name
                ? profileInformation?.name.trim()
                : profileInformation?.email
                ? profileInformation?.email
                : "-"
            }
            status={profileInformation?.status}
            goBack={() => history.goBack()}
          />
          {error && <DisplayPageError message={error.message} />}
          {!error && (
            <>
              <EditableInformation title={t("Information")}>
                <EditableInformationText
                  label="generic.name"
                  value={
                    profileInformation?.name
                      ? profileInformation?.name.trim()
                      : "-"
                  }
                  name="name"
                  editable={canEdit}
                  onSubmit={handleUpdateUser}
                />
                <EditableInformationText
                  label="generic.email"
                  value={profileInformation?.email}
                  name="email"
                  onSubmit={handleUpdateUser}
                />
                <EditableInformationPassword
                  label="generic.password"
                  value={"*********"}
                  passwordChangedWhen={
                    profileInformation?.passwordChangedAt
                      ? formatPasswordChangeDate(
                          profileInformation?.passwordChangedAt
                        )
                      : profileInformation?.createdAt
                      ? formatPasswordChangeDate(profileInformation?.createdAt)
                      : null
                  }
                  type="other"
                  userEmail={profileInformation?.email}
                />
              </EditableInformation>
              <div className="UserManageUser__content--divider"></div>
              <UserApps
                userId={profileInformation?.id}
                userRoles={profileInformation?.roles}
              />
            </>
          )}
        </div>
      </div>
      <div className="AdminDistributorEdit__right">
        <div className="AdminDistributorEdit__right--actions">
          <Button
            buttonStyle="Button__primary"
            disabled={false}
            click={() => {}}
          >
            {t("generic.save")}
          </Button>
          <Button
            buttonStyle="Button__secondary"
            click={() => history.goBack()}
          >
            {t("generic.cancel")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const getUserQuery = gql`
  query user($id: String) {
    user(id: $id) {
      id
      name
      email
      roles
      status
      createdAt
      passwordChangedAt
    }
  }
`;

const subscribeToUserUpdates = gql`
  subscription onSSOUserRemovedFromAccount($userId: String) {
    onSSOUserUpdated(userId: $userId) {
      id
      email
      name
      roles
      passwordChangedAt
    }
  }
`;

const updateUserOnSubData = ({ cache, subscriptionData, params }) => {
  const prev = cache.readQuery({
    query: getUserQuery,
    variables: { id: params.id },
  });
  const newData = {
    ...prev,
    user: {
      ...prev.user,
      roles: subscriptionData.data.onSSOUserUpdated.roles,
    },
  };
  cache.writeQuery({
    query: getUserQuery,
    variables: { id: params.id },
    data: newData,
  });
};

const updateUserInformationMutation = gql`
  mutation updateUser($id: ID!, $email: String, $name: String) {
    updateSSOUser(id: $id, email: $email, name: $name) {
      id
      name
      email
    }
  }
`;
